import { BackendConfig, Id, TreeNode } from './types'

const toQuery = (params: Record<string, string | number | boolean | null | undefined>): string => {
	const usp = new URLSearchParams()
	Object.entries(params).forEach(([k, v]) => {
		if (v === null || v === undefined) {
			return
		}
		usp.set(k, String(v))
	})
	const qs = usp.toString()
	return qs === '' ? '' : `&${qs}`
}

export const loadChildren = async (cfg: BackendConfig, parentId: Id | null | undefined): Promise<Array<TreeNode>> => {
	const url = `${cfg.endpoint}${toQuery({ detail: parentId as unknown as string | number | undefined })}`
	const data = await new Promise<Array<Record<string, unknown>>>((resolve, reject) => {
		// @ts-ignore - jQuery nette ajax is globally available via webpack ProvidePlugin
		$.nette.ajax({
			url,
			method: 'GET',
			dataType: 'json',
			success: (payload: unknown) => resolve((payload as Array<Record<string, unknown>>) ?? []),
			error: (_jq: unknown, textStatus: unknown, errorThrown: unknown) =>
				reject(new Error(`Failed to load children: ${String(textStatus ?? errorThrown)}`)),
		})
	})
	return data.map((row) => normalizeRow(row))
}

export const searchNodes = async (cfg: BackendConfig, term: string): Promise<Array<TreeNode>> => {
	const url = `${cfg.endpoint}${toQuery({ search: term })}`
	const data = await new Promise<Array<Record<string, unknown>>>((resolve, reject) => {
		// @ts-ignore
		$.nette.ajax({
			url,
			method: 'GET',
			dataType: 'json',
			success: (payload: unknown) => resolve((payload as Array<Record<string, unknown>>) ?? []),
			error: (_jq: unknown, textStatus: unknown, errorThrown: unknown) =>
				reject(new Error(`Failed to search: ${String(textStatus ?? errorThrown)}`)),
		})
	})
	return data.map((row) => normalizeRow(row))
}

export const saveSelectionSingle = async (cfg: BackendConfig, valueKey: string, value: Id | null): Promise<void> => {
	const url = `${cfg.endpoint}${toQuery({ [valueKey]: value })}`
	await new Promise<void>((resolve, reject) => {
		// @ts-ignore
		$.nette.ajax({
			url,
			method: 'GET',
			success: () => resolve(),
			error: (_jq: unknown, textStatus: unknown, errorThrown: unknown) =>
				reject(new Error(`Failed to save selection: ${String(textStatus ?? errorThrown)}`)),
		})
	})
}

export const saveSelectionMulti = async (
	cfg: BackendConfig,
	valueKey: string,
	values: Array<Id>,
	sentiments?: Record<string, number | null>,
): Promise<void> => {
	const joined = values.join(',')
	const params: Record<string, string | number | boolean | null | undefined> = { [valueKey]: joined }

	if (sentiments && Object.keys(sentiments).length > 0) {
		params.sentiments = JSON.stringify(sentiments)
	}

	const url = `${cfg.endpoint}${toQuery(params)}`
	await new Promise<void>((resolve, reject) => {
		// @ts-ignore
		$.nette.ajax({
			url,
			method: 'GET',
			success: () => resolve(),
			error: (_jq: unknown, textStatus: unknown, errorThrown: unknown) =>
				reject(new Error(`Failed to save selections: ${String(textStatus ?? errorThrown)}`)),
		})
	})
}

// Helper to map arbitrary backend rows into TreeNode
const normalizeRow = (row: Record<string, unknown>): TreeNode => {
	const id = (row.id ?? row.value ?? row.Id ?? row.ID) as Id
	const title = (row.title ?? row.name ?? row.Title ?? row.NAME) as string
	const hasChildren = Boolean(row.hasChildren ?? row.children ?? row.has_children ?? false)
	const parentId = (row.parentId ?? row.parent_id ?? null) as Id | null
	const sentiment = row.sentiment !== undefined ? (row.sentiment as number | null) : undefined
	const sentimentEnabled = row.sentimentEnabled !== undefined ? Boolean(row.sentimentEnabled) : undefined

	return {
		id,
		title,
		hasChildren,
		parentId,
		loaded: false,
		selected: false,
		...(sentiment !== undefined && { sentiment }),
		...(sentimentEnabled !== undefined && { sentimentEnabled }),
	}
}
